import { useTranslation } from "next-i18next";

import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { UsDeliveryFacts } from "@/features/us-delivery/components/UsDeliveryFacts";
import { UsDeliveryQuote } from "@/features/us-delivery/components/UsDeliveryQuote";
import { UsDeliveryKind } from "@/features/us-delivery/us-delivery.utils";
import { Flavor } from "@/ui/typography/Flavor";

type ProductDetailsUsDeliveryProps = {
  product: ProductBySlugNonNullable;
  kind: Exclude<UsDeliveryKind, "none">;
};

/**
 * WHO-6509. The product page's delivery block on `/en-us` for a listing that ships to the US:
 * the quote flow for a courier listing, then the delivery time and the duties note. Guests see it
 * too; only asking for a quote needs an account.
 *
 * A postal listing (`listed`) shows no price here yet. Today's options are priced for the
 * visitor's saved European location, which is wrong under a US header. The Sendcloud US rate
 * (WHO-6587) comes in as its own kind once the gateway serves it.
 */
export const ProductDetailsUsDelivery = ({
  product,
  kind,
}: ProductDetailsUsDeliveryProps) => {
  const { t } = useTranslation();

  return (
    <section
      className="flex flex-col gap-4 border border-grey-300 p-4"
      data-testid="pdp.us-delivery"
      data-us-delivery-kind={kind}
    >
      <Flavor size="medium" className="font-bold">
        {t("pdp:pdp-us-delivery-title", {
          defaultValue: "Shipping to the United States",
        })}
      </Flavor>
      {kind === "quote" && <UsDeliveryQuote product={product} />}
      <UsDeliveryFacts />
    </section>
  );
};
